import { jwtDecode } from 'jwt-decode';

/**
 * JWT Token Helpers for Password Reset Flow
 * Uses jwt-decode library for secure and reliable JWT parsing
 * Used to parse SessionId JWT tokens from ResetPasswordConfirmation API
 */

interface JWTPayload {
  si: string; // systemId
  exp?: number; // expiration
  iat?: number; // issued at
  [key: string]: unknown;
}

/**
 * Parse JWT token payload using jwt-decode library
 * This is for extracting systemId from trusted API responses
 */
export function parseJWTPayload(token: string): JWTPayload {
  try {
    return jwtDecode<JWTPayload>(token);
  } catch (error) {
    throw new Error(`Failed to parse JWT payload: ${error}`);
  }
}

/**
 * JWT validation - checks structure and expiration using jwt-decode
 * Does not verify signature (server-side responsibility)
 */
export function isJWTValid(token: string): boolean {
  try {
    const payload = jwtDecode<JWTPayload>(token);

    // Check expiration if present (including exp: 0)
    if (payload.exp !== undefined) {
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp < now) {
        return false;
      }
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Extract systemId from JWT SessionId using jwt-decode
 * Used in password reset flow after token validation
 */
export function extractSystemIdFromJWT(sessionId: string): string {
  const payload = jwtDecode<JWTPayload>(sessionId);
  if (!payload.si) {
    throw new Error('SystemId not found in JWT payload');
  }
  return payload.si;
}
